/**
 * TunnelConnector — expose local ports to the internet via cloudflare/ngrok/mock.
 * Category: service
 */

import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";
import { portableSpawn } from "@skaile/workspaces/core";

interface TunnelInstance {
  id: string;
  port: number;
  publicUrl: string;
  process?: { kill(): void };
  startedAt: string;
  status: "connected" | "disconnected";
}

interface TunnelState {
  provider: string;
  tunnels: Map<string, TunnelInstance>;
}

/**
 * Connector for exposing local ports to the internet via Cloudflare Tunnel or ngrok.
 * Manages tunnel lifecycle (expose, close, status) and supports a mock provider for testing.
 * @docLink packages/factory-assets/concepts#tunnel-connector
 */
export class TunnelConnector extends AbstractConnector {
  readonly name = "tunnel";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    const provider = String(declaration.options?.provider ?? "cloudflare");
    this.log.info("connect", { id: declaration.id, provider });
    if (provider !== "mock") {
      const cli = provider === "cloudflare" ? "cloudflared" : "ngrok";
      const found = await checkCliExists(cli);
      if (!found) {
        this.log.error("connect failed: CLI not found", undefined, { cli });
        throw new Error(`TunnelConnector: "${cli}" not found on PATH.`);
      }
    }
    return this.makeHandle(declaration, { provider, tunnels: new Map() } as TunnelState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    try {
      await this._closeAll(handle);
    } finally {
      handle.status = "disconnected";
    }
  }

  override async onSessionClose(handle: ConnectorHandle): Promise<string | undefined> {
    await this._closeAll(handle);
    return undefined;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _closeAll(handle: ConnectorHandle): Promise<void> {
    const state = handle.state as TunnelState;
    for (const [id, tunnel] of state.tunnels) {
      tunnel.process?.kill();
      tunnel.status = "disconnected";
      this.emitChange?.({ path: id, action: "delete", source: "operation" });
    }
    state.tunnels.clear();
  }

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "expose",
        description: "Create a tunnel to a local port.",
        accessLevel: "write",
        args: [
          { name: "port", type: "number", required: true, description: "Local port" },
          { name: "label", type: "string", required: false, description: "Tunnel label" },
          {
            name: "subdomain",
            type: "string",
            required: false,
            description: "Requested subdomain",
          },
        ],
      },
      {
        name: "close",
        description: "Tear down a tunnel.",
        accessLevel: "write",
        args: [{ name: "tunnel", type: "string", required: true, description: "Tunnel ID" }],
      },
      {
        name: "status",
        description: "Get tunnel status.",
        accessLevel: "read",
        args: [
          {
            name: "tunnel",
            type: "string",
            required: false,
            description: "Tunnel ID (omit for all)",
          },
        ],
      },
      { name: "list", description: "List all active tunnels.", accessLevel: "read", args: [] },
    ];
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const state = handle.state as TunnelState;
    if ((operation === "expose" || operation === "close") && handle.access === "read-only") {
      throw new Error(`TunnelConnector: "${operation}" requires read-write access`);
    }

    switch (operation) {
      case "expose": {
        const port = Number(args.port);
        if (!port || Number.isNaN(port)) throw new Error("expose requires a valid port");
        const label = args.label ? String(args.label) : String(port);
        let publicUrl: string;
        let proc: TunnelInstance["process"] | undefined;
        if (state.provider === "mock") {
          publicUrl = `http://mock-tunnel-${label}.localhost`;
        } else if (state.provider === "cloudflare") {
          const result = await spawnCloudflaredTunnel(port);
          publicUrl = result.url;
          proc = result.process;
        } else if (state.provider === "ngrok") {
          const result = await spawnNgrokTunnel(port);
          publicUrl = result.url;
          proc = result.process;
        } else throw new Error(`Unknown provider "${state.provider}"`);
        const tunnel: TunnelInstance = {
          id: label,
          port,
          publicUrl,
          process: proc,
          startedAt: new Date().toISOString(),
          status: "connected",
        };
        state.tunnels.set(label, tunnel);
        this.log.info("tunnel exposed", { label, port, publicUrl });
        this.emitChange?.({ path: label, action: "create", source: "operation" });
        return JSON.stringify({ tunnel_id: label, public_url: publicUrl });
      }
      case "close": {
        const id = String(args.tunnel ?? "");
        const tunnel = state.tunnels.get(id);
        if (!tunnel) throw new Error(`Tunnel "${id}" not found`);
        tunnel.process?.kill();
        tunnel.status = "disconnected";
        state.tunnels.delete(id);
        this.emitChange?.({ path: id, action: "delete", source: "operation" });
        return JSON.stringify({ closed: true });
      }
      case "status": {
        if (args.tunnel) {
          const t = state.tunnels.get(String(args.tunnel));
          if (!t) throw new Error(`Tunnel "${args.tunnel}" not found`);
          return JSON.stringify(tunnelToStatus(t));
        }
        return JSON.stringify([...state.tunnels.values()].map(tunnelToStatus));
      }
      case "list":
        return JSON.stringify(
          [...state.tunnels.values()].map((t) => ({
            id: t.id,
            port: t.port,
            public_url: t.publicUrl,
            status: t.status,
            started_at: t.startedAt,
          })),
        );
      default:
        throw new Error(`TunnelConnector: unknown operation "${operation}"`);
    }
  }

  private _describeSkill(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const state = handle.state as TunnelState;
    return {
      id: declaration.id,
      name: `Tunnel — ${state.provider}`,
      description: `Expose local ports to the internet via ${state.provider}.`,
      category: "service",
      access: handle.access,
      toolNames: this._describeOperations(handle).map((o) => o.name),
    };
  }
}

function tunnelToStatus(t: TunnelInstance) {
  return {
    id: t.id,
    port: t.port,
    public_url: t.publicUrl,
    status: t.status,
    started_at: t.startedAt,
    uptime_ms: Date.now() - new Date(t.startedAt).getTime(),
  };
}

async function checkCliExists(cli: string): Promise<boolean> {
  try {
    const proc = portableSpawn(["which", cli]);
    return (await proc.exited) === 0;
  } catch {
    return false;
  }
}

async function spawnCloudflaredTunnel(
  port: number,
): Promise<{ url: string; process: { kill(): void } }> {
  return new Promise((resolve, reject) => {
    const proc = portableSpawn(["cloudflared", "tunnel", "--url", `http://localhost:${port}`]);
    const decoder = new TextDecoder();
    const reader = proc.stderr.getReader();
    const readLoop = async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const match = chunk.match(/https:\/\/[^\s"]+\.trycloudflare\.com/);
        if (match) {
          resolve({ url: match[0], process: proc });
          return;
        }
      }
      proc.kill();
      reject(new Error("cloudflared: failed to parse public URL"));
    };
    readLoop().catch(reject);
    setTimeout(() => {
      proc.kill();
      reject(new Error("cloudflared: timed out"));
    }, 30_000);
  });
}

async function spawnNgrokTunnel(port: number): Promise<{ url: string; process: { kill(): void } }> {
  const proc = portableSpawn(["ngrok", "http", String(port)]);
  const deadline = Date.now() + 15_000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 500));
    try {
      const res = await fetch("http://localhost:4040/api/tunnels");
      if (res.ok) {
        const data = (await res.json()) as { tunnels?: Array<{ public_url: string }> };
        const t = data.tunnels?.find((t) => t.public_url.startsWith("https://"));
        if (t) return { url: t.public_url, process: proc };
      }
    } catch {}
  }
  proc.kill();
  throw new Error("ngrok: timed out");
}

/**
 * Creates a new TunnelConnector instance.
 * @returns Configured TunnelConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#tunnel-connector-factory
 */
export function createConnector(): TunnelConnector {
  return new TunnelConnector();
}
